import { z } from 'zod';
import {
    CloverLifecyclePreflightGatewayOperation,
    CloverLifecyclePreflightOutcome,
    CloverLifecyclePreflightPayloadType,
    type CloverLifecyclePreflightNormalizedResult,
    type CloverLifecyclePreflightPayload,
    type CloverLifecyclePreflightRequestDetails,
    type CloverLifecyclePreflightRequestPayload,
    type CloverLifecyclePreflightResponseDetails,
    type CloverLifecyclePreflightResponsePayload,
} from '../../../types/ipg/clover/clover-lifecycle-preflight.types';
import { CloverTerminalSaleRequestDetailsSchema } from './clover-terminal-sale.schemas';

export const CloverLifecyclePreflightRequestDetailsSchema: z.ZodType<CloverLifecyclePreflightRequestDetails> =
    z.object({
        gateway_operation: z.literal(CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE),
        terminal_sale_request_details: CloverTerminalSaleRequestDetailsSchema,
    });

export const CloverLifecyclePreflightNormalizedResultSchema: z.ZodType<CloverLifecyclePreflightNormalizedResult> =
    z.object({
        gateway_operation: z.literal(CloverLifecyclePreflightGatewayOperation.TERMINAL_SALE),
        device_id: z.string().min(1),
        amount: z.number().int().min(1).max(999999999),
        external_payment_id: z.string().min(1),
        final: z.literal(true),
        capture: z.literal(true).optional(),
        external_reference_id: z.string().min(1).max(12).optional(),
        tip_amount: z.number().int().min(0).max(999999999).optional(),
    });

export const CloverLifecyclePreflightRecordedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecyclePreflightOutcome.RECORDED>;
    normalized_result: typeof CloverLifecyclePreflightNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.RECORDED),
    normalized_result: CloverLifecyclePreflightNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.RECORDED;
        }
    >
>;

export const CloverLifecyclePreflightRejectedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecyclePreflightOutcome.REJECTED>;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.REJECTED),
    message: z.string().min(1),
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.REJECTED;
        }
    >
>;

export const CloverLifecyclePreflightMalformedInputResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecyclePreflightOutcome.MALFORMED_INPUT>;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CloverLifecyclePreflightOutcome.MALFORMED_INPUT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CloverLifecyclePreflightResponseDetails,
        {
            readonly outcome: typeof CloverLifecyclePreflightOutcome.MALFORMED_INPUT;
        }
    >
>;

export const CloverLifecyclePreflightResponseDetailsSchema: z.ZodType<CloverLifecyclePreflightResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverLifecyclePreflightRecordedResponseDetailsSchema,
        CloverLifecyclePreflightRejectedResponseDetailsSchema,
        CloverLifecyclePreflightMalformedInputResponseDetailsSchema,
    ]);

export const CloverLifecyclePreflightRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverLifecyclePreflightPayloadType.REQUEST
    >;
    request_details: typeof CloverLifecyclePreflightRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CloverLifecyclePreflightPayloadType.REQUEST),
    request_details: CloverLifecyclePreflightRequestDetailsSchema,
}) satisfies z.ZodType<CloverLifecyclePreflightRequestPayload>;

export const CloverLifecyclePreflightResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverLifecyclePreflightPayloadType.RESPONSE
    >;
    response_details: typeof CloverLifecyclePreflightResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CloverLifecyclePreflightPayloadType.RESPONSE),
    response_details: CloverLifecyclePreflightResponseDetailsSchema,
}) satisfies z.ZodType<CloverLifecyclePreflightResponsePayload>;

export const CloverLifecyclePreflightPayloadSchema: z.ZodType<CloverLifecyclePreflightPayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CloverLifecyclePreflightRequestPayloadSchema,
        CloverLifecyclePreflightResponsePayloadSchema,
    ]);
